import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms';
import { Permissions } from 'app/core/constants/permissions';
import { RoleNames } from 'app/core/constants/role-names';
import { LogHelper } from 'app/core/helpers/log.helper';
import { environment } from '../../../../../../environments/environment';
import {
  ActivateCardsRequest,
  ArchiveWalletRequest,
  AvailableWalletsResponse,
  BankTransferMethod,
  FullPatientPaymentInfo,
  GetCardPinRequest,
  GetWalletBalanceRequest,
  PatientBasicInfo,
  PaymentMethod,
  UpdateCaxtonCardStateResult
} from 'app/core/models/patient.model';
import { SelectOption } from 'app/core/models/select-option.model';
import { CurrencyDecimalAmountPipe } from 'app/core/pipes/currency-decimal-amount.pipe';
import { AuthService } from 'app/core/services/auth.service';
import { PatientService } from 'app/core/services/patient.service';
import { PaymentService } from 'app/core/services/payment.service';
import { AlertService } from 'app/shared/alert/alert.service';
import {
  AutosuggestDropdownInputComponent
} from 'app/shared/autosuggest-dropdown-input/autosuggest-dropdown-input.component';
import { ConfirmActionModalComponent } from 'app/shared/confirm-action-modal/confirm-action-modal.component';
import { ModalComponent } from 'app/shared/modal/modal.component';
import {
  ConfirmWalletAssignmentModalComponent
} from './confirm-wallet-assignment-modal/confirm-wallet-assignment-modal.component';
import {
  GrantOnlineAccessToCaxtonComponent
} from './grant-online-access-to-caxton/grant-online-access-to-caxton.component';
import { CardAlreadyActiveModalComponent } from './card-already-active-modal/card-already-active-modal.component';

@Component({
  selector: 'app-payment-info-caxton-card',
  templateUrl: './payment-info-caxton-card.component.html',
  styleUrls: ['./payment-info-caxton-card.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class PaymentInfoCaxtonCardComponent implements OnInit, OnChanges {
  @ViewChild("confirmWalletAssignmentModal") confirmWalletAssignmentModal: ConfirmWalletAssignmentModalComponent;
  @ViewChild("grantOnlineAccessToCaxtonComponent") grantOnlineAccessToCaxtonComponent: GrantOnlineAccessToCaxtonComponent;
  @ViewChild("cardAlreadyActiveModal") cardAlreadyActiveModal: CardAlreadyActiveModalComponent;
  @ViewChild("assignWalletSelect") assignWalletSelect: AutosuggestDropdownInputComponent;
  @ViewChild('confirmActionModal') confirmActionModal: ConfirmActionModalComponent;
  @ViewChild("viewCardPinModal") viewCardPinModal: ModalComponent;

  @Input() paymentInfoForm: FormGroup;
  @Input() patientId: string;
  @Input() trialId: string;
  @Input() patientBasicInfo: PatientBasicInfo;
  @Input() patientPaymentInfo: FullPatientPaymentInfo;

  @Output('updated') updated = new EventEmitter();

  assignWalletOptions: SelectOption[] = [];
  cardsExistOnWallet: boolean = true;
  availableWalletsList: AvailableWalletsResponse[] = [];
  selectedWallet: AvailableWalletsResponse;
  linkingCardToTracker: boolean = false;
  archivingWallet: boolean = false;
  cardsActivated: boolean = false;
  cardsStolen: boolean = false;
  cardsLost: boolean = false;
  cardsBlocked: boolean = false;
  caxtonOnlineAccessGranted: boolean = false;
  isCardActivationProcessing: boolean = false;
  isWalletBalanceProcessing: boolean = false;
  isCardPinProcessing: boolean = false;

  walletBalance: string = '';
  balanceSeparator: string = '/';
  cardPinImageUrl: string;
  cardActionBusy: boolean = false;

  isDevelopmentEnv: boolean = false;
  creatingTestWallet: boolean = false;

  Permissions = Permissions;
  RoleNames = RoleNames;
  PaymentMethod = PaymentMethod;
  BankTransferMethod = BankTransferMethod;

  constructor(
    public authService: AuthService,
    private patientService: PatientService,
    private alertService: AlertService,
    private paymentService: PaymentService,
    private decimalPipe: CurrencyDecimalAmountPipe
  ) { }

  ngOnInit() {
    this.initializeAvailableWallets();
    this.setCardStatus();
    this.getWalletBalances();

    if (environment.environment === 'DEVELOPMENT' || environment.environment === 'STAGING') {
      this.isDevelopmentEnv = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['patientPaymentInfo']) {
      this.setCardStatus();
    }
  }

  setCardStatus() {
    this.caxtonOnlineAccessGranted = this.patientPaymentInfo.caxtonOnlineAccessGranted;
    this.cardsActivated = this.patientPaymentInfo.caxtonCardsActivated;
    this.cardsLost = this.patientPaymentInfo.caxtonCardsLost;
    this.cardsStolen = this.patientPaymentInfo.caxtonCardsStolen;
    this.cardsBlocked = this.patientPaymentInfo.caxtonCardsBlocked;

    this.cardActionBusy = false;
  }

  checkIfCardsExistOnWallet(event: string) {
    this.cardsExistOnWallet = true;
    const wallet = this.availableWalletsList.find(w => w.walletId == event);
    this.selectedWallet = wallet;
    this.confirmWalletAssignmentModal.selectedWallet = wallet.walletId;
    this.cardAlreadyActiveModal.selectedWallet = wallet;

    this.patientService.getCardsExistOnWallet(this.patientId, wallet.walletId, wallet.walletIsOnChildSubsidiary).subscribe({
      next: (response) => {
        this.cardsExistOnWallet = response.cardsExistOnWallet;
      },
      error: () => {
        this.cardsExistOnWallet = true;
      }
    });
  }

  initializeAvailableWallets() {
    if (this.availableWalletsList.length === 0 && this.patientPaymentInfo.subsidiaryLinkedToTrial) {
      this.getAvailableWallets();
    }
  }

  onConfirmWalletAssignmentResult(cardsAlreadyActive: boolean) {
    this.cardsActivated = false;
    this.caxtonOnlineAccessGranted = false;

    if (cardsAlreadyActive) {
      this.cardAlreadyActiveModal.modal.show();
    }
  }

  onWalletAssigned() {
    this.cardsActivated = false;
    this.caxtonOnlineAccessGranted = false;
  }

  getAvailableWallets() {
    this.patientService.availableWallets(this.trialId).subscribe({
      next: (response) => {
        if (!this.assignWalletOptions.length) {
          response.forEach((r) =>
            this.assignWalletOptions.push({
              value: r.walletId,
              text: r.walletName,
            })
          );
          this.availableWalletsList = response;
        }
      },
      error: (error) => {
        this.alertService.showWarningAlert("Failed to get wallets.");
        LogHelper.log(error);
      },
    });
  }

  createTestWalletOnChildSubsidiary() {
    if (!this.selectedWallet) {
      this.alertService.showWarningAlert("Please select a wallet.");
      return;
    }

    this.creatingTestWallet = true;

    this.patientService.createTestWalletOnChildSubsidiary(this.patientId, this.selectedWallet.walletId).subscribe({
      next: (result) => {
        this.creatingTestWallet = false;
        this.alertService.showSuccessAlert(`Created empty wallet "${result.walletName}" (id ${result.walletId}) on the child subsidiary.`);
        this.assignWalletOptions = [];
        this.availableWalletsList = [];
        this.getAvailableWallets();
      },
      error: (error) => {
        this.creatingTestWallet = false;
        this.alertService.showWarningAlert(error.error ? error.error.title : "Unable to create test wallet. Please try again.");
        LogHelper.log(error);
      },
    });
  }

  showWalletAssignmentPopup() {
    if (this.assignWalletSelect.selectedOption?.value) {
      this.confirmWalletAssignmentModal.modal.show();
      return;
    }

    this.alertService.showWarningAlert("Please select a wallet.");
  }

  onArchiveWalletClick() {
    this.confirmActionModal.showConfirmation('archive', 'Archive Wallet', 'Are you sure you want to archive this wallet? This action cannot be undone.');
  }

  archiveWallet() {
    this.archivingWallet = true;

    let request: ArchiveWalletRequest = {
      patientTrialId: this.patientId,
      walletId: this.selectedWallet.walletId,
      walletIsOnChildSubsidiary: this.selectedWallet.walletIsOnChildSubsidiary
    };

    this.patientService.archiveWallet(request).subscribe({
      next: (result) => {
        this.archivingWallet = false;

        if (result.updateSuccessful) {
          this.alertService.showSuccessAlert(result.message);
          this.availableWalletsList = this.availableWalletsList.filter(w => w.walletId !== this.selectedWallet.walletId);
          this.assignWalletOptions = this.assignWalletOptions.filter(o => o.value !== this.selectedWallet.walletId);
          this.selectedWallet = null;
          this.cardsExistOnWallet = true;
          this.assignWalletSelect.reset();
        } else {
          this.alertService.showWarningAlert(result.message);
        }
      },
      error: (error) => {
        this.archivingWallet = false;
        this.alertService.showWarningAlert(error.error ? error.error.title : "Unable to archive wallet. Please try again.");
        LogHelper.log(error);
      },
    });
  }

  linkCardToTracker() {
    this.linkingCardToTracker = true;
    this.patientService.linkCardToTracker(this.patientId).subscribe({
      next: rsp => {
        if (rsp.success) {
          this.alertService.showSuccessAlert(rsp.message);
        } else {
          this.alertService.showWarningAlert(rsp.message);
        }
        this.linkingCardToTracker = false;
      },
      error: err => {
        this.linkingCardToTracker = false;
        this.alertService.showWarningAlert(err)
      }
    });
  }

  itemVisible(roles: string[]): boolean {
    return this.authService.hasAnyRole(roles.join(','));
  }

  showGrantCaxtonAccessPopup() {
    this.grantOnlineAccessToCaxtonComponent.confirmGrantOnlineAccessToCaxtonModal.show();
  }

  activateCards() {
    this.isCardActivationProcessing = true;
    let walletId = this.paymentInfoForm.get("caxtonWalletId").value;

    let activateCardRequest: ActivateCardsRequest = {
      patientTrialId: this.patientId,
      walletId: walletId
    };

    this.patientService.activateCards(this.patientId, activateCardRequest)
      .subscribe({
        next: (result) => {
          this.isCardActivationProcessing = false;
          this.cardsActivated = result.cardsActivatedSuccessfully;
          this.caxtonOnlineAccessGranted = result.cardsOnlineAccessGranted;

          if (this.cardsActivated) {
            this.getWalletBalances();
          }
        },
        error: (error) => {
          this.alertService.showWarningAlert(error.error ? error.error.title : "Unable to activate cards. Please try again.");
          this.isCardActivationProcessing = false;
          LogHelper.log(error);
        },
      });
  }

  getWalletBalances() {
    if (this.cardsActivated) {
      this.isWalletBalanceProcessing = true;
      let walletId = this.paymentInfoForm.get("caxtonWalletId").value;

      let request = {
        patientTrialId: this.patientId,
        walletId: walletId
      } as GetWalletBalanceRequest

      this.paymentService.getWalletBalances(request).subscribe({
        next: result => {
          let formattedBalances = result.balances
            .filter(balance => balance.value !== 0)
            .map(balance => `${balance.symbol}${this.decimalPipe.transform(balance.value, '1.2-2', true)}`);

          this.walletBalance = formattedBalances.join(` ${this.balanceSeparator} `);

          if (formattedBalances.length === 0 && result.balances.length !== 0) {
            this.walletBalance = `0`;
          }

          this.isWalletBalanceProcessing = false;
        },
        error: () => {
          this.isWalletBalanceProcessing = false;
          this.walletBalance = 'N/A';
          this.alertService.showWarningAlert('There was a problem getting your account balance. Please retry and if problem persists contact administrator.');
        }
      });
    }
  }


  viewCardPin() {
    this.isCardPinProcessing = true;
    let walletId = this.paymentInfoForm.get("caxtonWalletId").value;

    let viewCardPinRequest: GetCardPinRequest = {
      patientTrialId: this.patientId,
      walletId: walletId
    };

    this.patientService.getCardPin(viewCardPinRequest).subscribe({
      next: (result) => {
        this.cardPinImageUrl = result.cardPinImageUrl;
        this.isCardPinProcessing = false;
        this.viewCardPinModal.show();
      },
      error: (error) => {
        this.alertService.showWarningAlert(error.error ? error.error.title : "Unable to retrieve card pin. Please try again.");
        this.isCardPinProcessing = false;
        LogHelper.log(error);
      },
    });
  }

  onHandleMarkCardUnblockedConfirmed() {
    this.cardActionBusy = true;
    let walletId = this.paymentInfoForm.get("caxtonWalletId").value;

    let activateCardRequest: ActivateCardsRequest = {
      patientTrialId: this.patientId,
      walletId: walletId
    };

    this.patientService.setCardUnblocked(this.patientId, activateCardRequest).subscribe({
      next: (result: UpdateCaxtonCardStateResult) => {
        console.log(result.updateSuccessful);
        if (result.updateSuccessful) {
          this.alertService.showSuccessAlert(result.message);
        } else {
          this.alertService.showWarningAlert(result.message);
        }
        this.updated.emit();
      },
      error: (error) => {
        this.alertService.showWarningAlert(error.error ? error.error.title : "Unable to unblock cards. Please try again.");
        this.cardActionBusy = false;
        LogHelper.log(error);
      },
    });
  }

  onHandleMarkCardUnblockedClick() {
    this.confirmActionModal.showConfirmation('unblock', 'Unblock Card', 'Are you sure you want to unblock this card?');
  }

  onHandleMarkCardBlockedConfirmed() {
    this.cardActionBusy = true;
    let walletId = this.paymentInfoForm.get("caxtonWalletId").value;

    let activateCardRequest: ActivateCardsRequest = {
      patientTrialId: this.patientId,
      walletId: walletId
    };

    this.patientService.setCardBlocked(this.patientId, activateCardRequest).subscribe({
      next: (result: UpdateCaxtonCardStateResult) => {
        if (result.updateSuccessful) {
          this.alertService.showSuccessAlert(result.message);
        } else {
          this.alertService.showWarningAlert(result.message);
        }
        this.updated.emit();
      },
      error: (error) => {
        this.alertService.showWarningAlert(error.error ? error.error.title : "Unable to block cards. Please try again.");
        this.cardActionBusy = false;
        LogHelper.log(error);
      },
    });
  }

  onHandleMarkCardBlockedClick() {
    this.confirmActionModal.showConfirmation('block', 'Block Card', 'Are you sure you want to block this card?');
  }

  onHandleMarkCardStolenConfirmed() {
    this.cardActionBusy = true;
    let walletId = this.paymentInfoForm.get("caxtonWalletId").value;

    let activateCardRequest: ActivateCardsRequest = {
      patientTrialId: this.patientId,
      walletId: walletId
    };

    this.patientService.setCardStolen(this.patientId, activateCardRequest).subscribe({
      next: (result: UpdateCaxtonCardStateResult) => {
        if (result.updateSuccessful) {
          this.alertService.showSuccessAlert(result.message);
        } else {
          this.alertService.showWarningAlert(result.message);
        }
        this.updated.emit();
        this.assignWalletSelect.reset();
      },
      error: (error) => {
        this.alertService.showWarningAlert(error.error ? error.error.title : "Unable to mark cards as stolen. Please try again.");
        this.cardActionBusy = false;
        LogHelper.log(error);
      },
    });
  }

  onHandleMarkCardStolenClick() {
    this.confirmActionModal.showConfirmation('stolen', 'Mark Card Stolen', 'Are you sure you want to mark this card as stolen?');
  }

  onHandleMarkCardLostConfirmed() {
    this.cardActionBusy = true;
    let walletId = this.paymentInfoForm.get("caxtonWalletId").value;

    let activateCardRequest: ActivateCardsRequest = {
      patientTrialId: this.patientId,
      walletId: walletId
    };

    this.patientService.setCardLost(this.patientId, activateCardRequest).subscribe({
      next: (result: UpdateCaxtonCardStateResult) => {
        if (result.updateSuccessful) {
          this.alertService.showSuccessAlert(result.message);
        } else {
          this.alertService.showWarningAlert(result.message);
        }
        this.updated.emit();
      },
      error: (error) => {
        this.alertService.showWarningAlert(error.error ? error.error.title : "Unable to mark cards as lost. Please try again.");
        this.cardActionBusy = false;
        LogHelper.log(error);
      },
    });
  }

  onHandleMarkCardLostClick() {
    this.confirmActionModal.showConfirmation('lost', 'Mark Card Lost', 'Are you sure you want to mark this card as lost?');
  }

  onCardActionConfirmed(action: string) {
    switch (action) {
      case 'lost':
        this.onHandleMarkCardLostConfirmed();
        break;
      case 'stolen':
        this.onHandleMarkCardStolenConfirmed();
        break;
      case 'block':
        this.onHandleMarkCardBlockedConfirmed();
        break;
      case 'unblock':
        this.onHandleMarkCardUnblockedConfirmed();
        break;
      case 'archive':
        this.archiveWallet();
        break;
    }
  }

  onCaxtonOnlineAccessGranted(granted: boolean) {
    this.caxtonOnlineAccessGranted = granted;
  }
}
